import type { InstanceParticipant } from '../use-instance-participants';
import { FloorManagerBadge } from './FloorManagerBadge';

interface Props {
  readonly participants: readonly InstanceParticipant[];
  /**
   * Discord user ID of the currently-authenticated viewer — used to tag their
   * own chip with a "(you)" marker so the strip reads as "me + the others
   * here," not an undifferentiated row of avatars.
   */
  readonly currentUserId: string;
  /**
   * Discord user IDs of every currently on-duty Floor Manager (#5169). Any
   * co-present participant whose id is in this list renders the compact-variant
   * `<FloorManagerBadge>`. Empty when no FM is on-duty or none of the on-duty
   * FMs have linked Discord — the strip still renders normally; nobody gets
   * the badge.
   */
  readonly floorManagerDiscordUserIds: readonly string[];
}

/**
 * Co-presence strip rendered above the consolidated Activity surface
 * (#4103). Sourced from the Discord Embedded App SDK's
 * `getInstanceConnectedParticipants` + `ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE`
 * pipeline (see `use-instance-participants.ts`), which means every chip is a
 * person currently looking at the same Activity instance — not a tester-
 * availability roster (that's `<PresenceRoster>`, orthogonal).
 *
 * Distinction from `<PresenceRoster>`:
 *   - PresenceRoster = "who in the tester org could claim work right now"
 *   - InstanceParticipantsHeader = "who is in this Activity with me, right now"
 *
 * The strip purposefully includes the viewer's own chip — co-presence reads
 * as "us" not "them," and the (you) marker keeps the chip easy to locate at
 * a glance. Bot participants are filtered upstream in the hook.
 *
 * If the SDK ever returns an empty list (no participants at all — shouldn't
 * happen in practice, the viewer is always present) the strip renders
 * nothing rather than an empty bar. The header is non-essential chrome.
 */
export function InstanceParticipantsHeader({
  participants,
  currentUserId,
  floorManagerDiscordUserIds,
}: Props) {
  if (participants.length === 0) return null;

  return (
    <div className="instance-participants-header" aria-label="People in this Activity">
      <span className="instance-participants-header__label">Here with you</span>
      <ul className="instance-participants-header__list">
        {participants.map((p) => {
          const isSelf = p.id === currentUserId;
          const isFloorManager = floorManagerDiscordUserIds.includes(p.id);
          return (
            <li
              key={p.id}
              className={chipClass({ isSelf, isFloorManager })}
            >
              <img
                className="instance-participants-header__avatar"
                src={p.avatarUrl}
                alt=""
                width={20}
                height={20}
              />
              {isFloorManager && <FloorManagerBadge variant="compact" />}
              <span className="instance-participants-header__name">
                {p.displayName}
                {isSelf ? <span className="instance-participants-header__self"> (you)</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function chipClass({
  isSelf,
  isFloorManager,
}: {
  readonly isSelf: boolean;
  readonly isFloorManager: boolean;
}): string {
  const classes = ['instance-participants-header__chip'];
  if (isSelf) classes.push('instance-participants-header__chip--self');
  if (isFloorManager) classes.push('instance-participants-header__chip--fm');
  return classes.join(' ');
}
